import React, { useMemo, useState } from "react";
import {
  FiActivity,
  FiInfo,
  FiAlertTriangle,
  FiXOctagon,
  FiRotateCcw,
  FiDownload,
  FiInbox,
} from "react-icons/fi";
import {
  tokens,
  PageShell,
  PageWidth,
  PageHeader,
  Button,
  Toolbar,
  Spacer,
  SearchInput,
  Select,
  StatGrid,
  StatCard,
  TableCard,
  TableScroll,
  Table,
  Th,
  Td,
  Tr,
  Badge,
  Tag,
  EmptyState,
  Pagination,
} from "../../../components/admin/AdminUI";
import { dummyLogs, logLevels, logCategories } from "./dummyData";

const PAGE_SIZE = 10;

const LEVEL_META = {
  INFO: { tone: "brand", label: "정보" },
  WARN: { tone: "warning", label: "경고" },
  ERROR: { tone: "danger", label: "오류" },
};

export default function SystemLog() {
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);

  const filteredLogs = useMemo(() => {
    const keyword = appliedSearch.trim().toLowerCase();
    return dummyLogs.filter((log) => {
      if (levelFilter !== "ALL" && log.level !== levelFilter) return false;
      if (categoryFilter !== "ALL" && log.category !== categoryFilter) return false;
      if (keyword) {
        const haystack = `${log.user} ${log.action} ${log.detail} ${log.ip}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [levelFilter, categoryFilter, appliedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedLogs = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const stats = useMemo(
    () => ({
      total: dummyLogs.length,
      info: dummyLogs.filter((l) => l.level === "INFO").length,
      warn: dummyLogs.filter((l) => l.level === "WARN").length,
      error: dummyLogs.filter((l) => l.level === "ERROR").length,
    }),
    []
  );

  const handleSearch = () => {
    setAppliedSearch(searchValue);
    setPage(1);
  };

  const handleReset = () => {
    setLevelFilter("ALL");
    setCategoryFilter("ALL");
    setSearchValue("");
    setAppliedSearch("");
    setPage(1);
  };

  const isFiltered =
    levelFilter !== "ALL" || categoryFilter !== "ALL" || appliedSearch !== "";

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          eyebrow="SYSTEM"
          title="시스템 로그"
          subtitle="시스템 활동 및 이벤트 기록을 조회하고 모니터링합니다."
          actions={
            <Button $variant="secondary" onClick={() => window.print()}>
              <FiDownload /> 내보내기
            </Button>
          }
        />

        <StatGrid $cols={4}>
          <StatCard
            label="전체 로그"
            value={stats.total}
            hint="최근 수집된 전체 이벤트"
            icon={<FiActivity />}
          />
          <StatCard
            label="정보 (INFO)"
            value={stats.info}
            icon={<FiInfo />}
            color={tokens.color.primary}
            soft={tokens.color.primarySoft}
          />
          <StatCard
            label="경고 (WARN)"
            value={stats.warn}
            icon={<FiAlertTriangle />}
            color={tokens.color.warningText}
            soft={tokens.color.warningSoft}
          />
          <StatCard
            label="오류 (ERROR)"
            value={stats.error}
            icon={<FiXOctagon />}
            color={tokens.color.dangerText}
            soft={tokens.color.dangerSoft}
          />
        </StatGrid>

        <Toolbar>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            placeholder="사용자, 활동, IP, 상세 내용 검색"
            grow="1 1 280px"
          />
          <Select
            value={levelFilter}
            onChange={(v) => {
              setLevelFilter(v);
              setPage(1);
            }}
            options={logLevels}
            minWidth="140px"
          />
          <Select
            value={categoryFilter}
            onChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
            options={logCategories}
            minWidth="160px"
          />
          <Spacer />
          {isFiltered && (
            <Button $variant="ghost" onClick={handleReset}>
              <FiRotateCcw /> 초기화
            </Button>
          )}
        </Toolbar>

        <TableCard>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 168 }}>시간</Th>
                  <Th style={{ width: 92 }}>레벨</Th>
                  <Th style={{ width: 116 }}>카테고리</Th>
                  <Th style={{ width: 120 }}>사용자</Th>
                  <Th>활동</Th>
                  <Th style={{ width: 140 }}>IP 주소</Th>
                  <Th>상세</Th>
                </tr>
              </thead>
              <tbody>
                {pagedLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState
                        icon={<FiInbox />}
                        title="조회된 로그가 없습니다"
                        description={
                          isFiltered
                            ? "필터 조건과 일치하는 로그가 없습니다. 조건을 변경해 보세요."
                            : "아직 수집된 시스템 로그가 없습니다."
                        }
                        action={
                          isFiltered ? (
                            <Button $variant="secondary" onClick={handleReset}>
                              <FiRotateCcw /> 필터 초기화
                            </Button>
                          ) : null
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  pagedLogs.map((log) => {
                    const meta = LEVEL_META[log.level] || { tone: "neutral", label: log.level };
                    return (
                      <Tr key={log.id}>
                        <Td $muted style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {log.timestamp}
                        </Td>
                        <Td>
                          <Badge $tone={meta.tone} $dot>
                            {meta.label}
                          </Badge>
                        </Td>
                        <Td>
                          <Tag>{log.category}</Tag>
                        </Td>
                        <Td style={{ fontWeight: 600, color: tokens.color.heading }}>
                          {log.user}
                        </Td>
                        <Td>{log.action}</Td>
                        <Td $muted style={{ fontVariantNumeric: "tabular-nums" }}>
                          {log.ip}
                        </Td>
                        <Td $muted>{log.detail}</Td>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableScroll>
          {pagedLogs.length > 0 && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onChange={setPage}
              totalItems={filteredLogs.length}
              pageSize={PAGE_SIZE}
            />
          )}
        </TableCard>
      </PageWidth>
    </PageShell>
  );
}
